import PropTypes from 'prop-types';

export const CuratedMonthlyPayment = PropTypes.shape({ estimatedMonthlyPayment: PropTypes.number }).isRequired;

export const CapitalizedFees = PropTypes.shape({
  titleFee: PropTypes.number,
  registrationFee: PropTypes.number,
  dmvFee: PropTypes.number,
  combinedFees: PropTypes.number,
  acquisitionFee: PropTypes.number,
  docFee: PropTypes.number,
});

export const CapitalizedTaxes = PropTypes.shape({
  citySalesTax: PropTypes.number,
  combinedSalesTax: PropTypes.number,
  countySalesTax: PropTypes.number,
  districtSalesTax: PropTypes.number,
  stateSalesTax: PropTypes.number,
  gasGuzzlerTax: PropTypes.number,
});

export const PaymentWithTaxes = PropTypes.shape({
  adjustedResidualAmount: PropTypes.number,
  amountFinanced: PropTypes.number,
  baseMonthlyPayment: PropTypes.number,
  capitalizedFees: CapitalizedFees.isRequired,
  capitalizedTaxes: CapitalizedTaxes.isRequired,
  dueAtSigning: PropTypes.number,
  inceptionFees: PropTypes.number,
  monthlyPayment: PropTypes.number.isRequired,
  monthlySalesTax: PropTypes.number,
  propertyTax: PropTypes.number,
  securityDeposit: PropTypes.number,
  totalDownPayment: PropTypes.number,
});

export const Incentive = PropTypes.shape({
  cash: PropTypes.number,
  incentiveId: PropTypes.number,
  programId: PropTypes.string,
});

export const DigitalRetailOffer = PropTypes.shape({
  acquisitionFee: PropTypes.number,
  annualMileage: PropTypes.number,
  docFee: PropTypes.number,
  endDate: PropTypes.number,
  excessMileageFee: PropTypes.number,
  financierName: PropTypes.string,
  incentives: PropTypes.arrayOf(Incentive),
  moneyFactor: PropTypes.number,
  monthlyPayment: PropTypes.number,
  programName: PropTypes.string,
  residual: PropTypes.number,
  securityDeposit: PropTypes.number,
  sellingPrice: PropTypes.number,
  startDate: PropTypes.number,
  styleId: PropTypes.number,
  term: PropTypes.number,
  totalRebate: PropTypes.number,
  turnInFee: PropTypes.number,
});

export const DigitalRetailOffers = PropTypes.shape({
  annualMileage: PropTypes.objectOf(PropTypes.arrayOf(DigitalRetailOffer)),
});

const CalculatedOfferProps = {
  acquisitionFee: PropTypes.number,
  annualMileage: PropTypes.number,
  docFee: PropTypes.number,
  endDate: PropTypes.number,
  excessMileageFee: PropTypes.number,
  financierName: PropTypes.string,
  incentives: PropTypes.arrayOf(Incentive),
  moneyFactor: PropTypes.number,
  monthlyPayment: PropTypes.number,
  programName: PropTypes.string,
  residual: PropTypes.number,
  securityDeposit: PropTypes.number,
  sellingPrice: PropTypes.number,
  startDate: PropTypes.number,
  styleId: PropTypes.number,
  term: PropTypes.number,
  totalRebate: PropTypes.number,
  turnInFee: PropTypes.number,
  apr: PropTypes.number,
  downPayment: PropTypes.number,
  dueAtSigning: PropTypes.number,
  isVerified: PropTypes.bool,
  netCapCost: PropTypes.number,
  paymentWithTaxes: PaymentWithTaxes,
  endDateEpochMillis: PropTypes.number,
  startDateEpochMillis: PropTypes.number,
  tradeIn: PropTypes.number,
};

export const CalculatedOffer = PropTypes.shape(CalculatedOfferProps);

export const VerifiedOffer = PropTypes.shape({
  ...CalculatedOfferProps,
  initialDueAtSigning: PropTypes.number,
  initialMonthlyPayment: PropTypes.number,
});

export const CreditResponse = PropTypes.shape({
  reportUrl: PropTypes.string.isRequired,
  result: PropTypes.string.isRequired,
  transId: PropTypes.string.isRequired,
});

export const Location = PropTypes.shape({
  pathname: PropTypes.string.isRequired,
  search: PropTypes.string,
  hash: PropTypes.string,
});

export const DealerMapping = PropTypes.shape({
  addOns: PropTypes.string,
  mdocaId: PropTypes.string,
  routeOneDealerId: PropTypes.string,
  makeName: PropTypes.string,
  makeNiceId: PropTypes.string,
  name: PropTypes.string,
  rooftopId: PropTypes.string,
  address: PropTypes.shape({
    city: PropTypes.string,
    stateCode: PropTypes.string,
    stateName: PropTypes.string,
    street: PropTypes.string,
    zip: PropTypes.string,
  }),
});

export const DecryptedProfile = PropTypes.shape({
  additionalAddress: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string,
  deliveryAddress: PropTypes.string,
  deliveryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  deliveryOption: PropTypes.string,
  deliveryTime: PropTypes.string,
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  phoneNumber: PropTypes.string,
  state: PropTypes.string,
  zipCode: PropTypes.string,
});

export const StoredProfile = PropTypes.shape({
  expiration: PropTypes.number,
  profile: PropTypes.string,
});

export const StepItem = PropTypes.shape({
  label: PropTypes.string,
  nextPath: PropTypes.string,
  path: PropTypes.string,
});

export const DealerData = PropTypes.shape({
  city: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  stateCode: PropTypes.string.isRequired,
  street: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired,
});

export const TransformedDealerInfo = PropTypes.shape({
  dealerData: DealerData,
});

export const LeaseDeal = PropTypes.shape({
  curatedMonthlyPayment: CuratedMonthlyPayment,
  makeName: PropTypes.string.isRequired,
  makeNiceId: PropTypes.string.isRequired,
  modelName: PropTypes.string.isRequired,
  modelNiceId: PropTypes.string.isRequired,
  photoUrl: PropTypes.string,
  stockNumber: PropTypes.string.isRequired,
  style: PropTypes.string.isRequired,
  vehicleCategoryTypeName: PropTypes.string.isRequired,
  vehicleCategoryTypeNiceId: PropTypes.string.isRequired,
  vin: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
});

export const LandingTab = PropTypes.arrayOf(
  PropTypes.shape({
    inventoryCount: PropTypes.number,
    name: PropTypes.string.isRequired,
    niceId: PropTypes.string.isRequired,
  })
);

export const MakeModel = PropTypes.shape({
  name: PropTypes.string,
  niceId: PropTypes.string,
});

export const UserIncentives = PropTypes.shape({
  collegeGrad: PropTypes.bool,
  collegeStudent: PropTypes.bool,
  conquest: PropTypes.bool,
  loyalty: PropTypes.bool,
  military: PropTypes.bool,
});

export const QualificationResultsMessage = PropTypes.oneOfType([PropTypes.node, PropTypes.string]);

export const QualificationResults = PropTypes.shape({
  callToAction: PropTypes.string,
  message: QualificationResultsMessage.isRequired,
  showDetails: PropTypes.bool,
  title: PropTypes.string.isRequired,
});

export const CalculatorConfig = PropTypes.shape({
  mileagesMapping: PropTypes.shape({}),
  termsMapping: PropTypes.shape({}),
});

export const TradeInDetails = PropTypes.shape({
  appraisalValue: PropTypes.number,
  amountOwed: PropTypes.number,
  appraisalVehicle: PropTypes.string,
  tradeInLimitDescription: PropTypes.string,
  tradeInEquityDescription: PropTypes.string,
  appliedTradeInEquity: PropTypes.number,
  tradeInEquity: PropTypes.number,
});
